package com.iregained.site.components.sections.resources.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.resources.styles.BrochureSubmitButtonStyle
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.region.Region
import com.iregained.site.region.RegionConfig
import com.iregained.site.theme.SitePalette
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.required
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLInputElement
import org.w3c.fetch.NO_CORS
import org.w3c.fetch.RequestInit
import kotlin.js.json

external fun encodeURIComponent(str: String): String

// UNIQUE ANIMATION: Book Unfold
@Composable
private fun Modifier.bookUnfold(isVisible: Boolean, delayMs: Int = 0): Modifier {
    return this.opacity(if (isVisible) 1 else 0).rotateX(if (isVisible) 0.deg else (-30).deg)
        .translateY(if (isVisible) 0.px else 40.px)
        .styleModifier { property("transform-origin", "top center"); property("perspective", "1200px") }.transition(
            Transition.of("opacity", 0.8.s, TransitionTimingFunction.EaseOut, delayMs.ms),
            Transition.of("transform", 0.8.s, TransitionTimingFunction.EaseOut, delayMs.ms)
        )
}

@Composable
fun ResourcesBrochureDownloadSection(data: ResourcesBrochureDownloadData = defaultResourcesBrochureDownloadData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "resources-brochure-download"

    var isSubmitting by remember { mutableStateOf(false) }
    var formSubmitted by remember { mutableStateOf(false) }
    var isVisible by remember { mutableStateOf(false) }

    val parsedFonts = data.fontFamily.split(",").map { it.trim() }.toTypedArray()

    Column(
        modifier = Modifier.background(Colors.White).id(targetId).backgroundColor(Colors.White)
            .styleModifier { property("scroll-margin-top", "100px") }.fillMaxWidth().padding(
                top = if (isDesktop) 60.px else 40.px, bottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent
            ), horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Div(
            attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center).toAttrs {
                ref { element ->
                    val observer = IntersectionObserver { entries, obs ->
                        entries.forEach {
                            if (it.isIntersecting) {
                                isVisible = true; obs.unobserve(element)
                            }
                        }
                    }
                    observer.observe(element); onDispose { observer.disconnect() }
                }
            }) {
            Column(
                modifier = Modifier.fillMaxWidth().maxWidth(1200.px), horizontalAlignment = Alignment.CenterHorizontally
            ) {
                if (data.mainHeading.isNotBlank()) {
                    SectionHeader(
                        text = data.mainHeading,
                        fontSize = 32.px,
                        lineWidthPercent = 25.percent,
                        modifier = Modifier.margin(bottom = 16.px).opacity(if (isVisible) 1 else 0)
                            .transition(Transition.of("opacity", 0.8.s)),
                        alignment = Alignment.CenterHorizontally
                    )
                }
                if (data.subHeading.isNotBlank()) {
                    Div(
                        attrs = SubheadlineTextStyle.toModifier().maxWidth(1200.px).fontSize(18.px)
                            .fontWeight(FontWeight.Medium).color(palette.custom.darkText).textAlign(TextAlign.Center)
                            .margin(bottom = 40.px).opacity(if (isVisible) 1 else 0)
                            .transition(Transition.of("opacity", 0.8.s, delay = 150.ms))
                            .toAttrs { ref { htmlElement -> htmlElement.innerHTML = data.subHeading; onDispose {} } })
                }

                Box(
                    modifier = Modifier.fillMaxWidth().backgroundColor(palette.custom.lightGray).borderRadius(16.px)
                        .borderTop(8.px, LineStyle.Solid, palette.brand.primary)
                        .boxShadow(0.px, 20.px, 45.px, 0.px, Colors.Black.copyf(alpha = 0.15f))
                        .padding(if (isDesktop) 50.px else 25.px).bookUnfold(isVisible, 300) // Card unfolds into view
                ) {
                    SimpleGrid(
                        numColumns(1, lg = 2), modifier = Modifier.fillMaxWidth().gap(if (isDesktop) 60.px else 40.px)
                    ) {
                        Column(modifier = Modifier.fillMaxWidth()) {
                            if (data.cardTitle.isNotBlank()) SpanText(
                                text = data.cardTitle,
                                modifier = Modifier.fontFamily(*parsedFonts).fontSize(24.px).fontWeight(FontWeight.Bold)
                                    .color(palette.custom.darkText).margin(bottom = 8.px)
                            )
                            if (data.cardSubtitle.isNotBlank()) SpanText(
                                text = data.cardSubtitle,
                                modifier = Modifier.fontFamily(*parsedFonts).fontSize(15.px)
                                    .color(palette.custom.darkText.toRgb().copyf(alpha = 0.8f)).margin(bottom = 24.px)
                            )

                            Form(attrs = Modifier.fillMaxWidth().toAttrs {
                                attr("action", data.formProcessingEndpoint); attr("method", "POST")
                                onSubmit { e ->
                                    e.preventDefault()
                                    isSubmitting = true

                                    // 1. Grab the honeypot value
                                    val honeypot = (document.getElementById("brochure_website_url_honeypot") as? HTMLInputElement)?.value ?: ""

                                    // 2. Fast-fail on frontend to save network calls
                                    if (honeypot.isNotEmpty()) {
                                        isSubmitting = false
                                        formSubmitted = true
                                        return@onSubmit
                                    }

                                    // 3. Initialize formData with the form ID AND the honeypot
                                    var formData = "form_id=brochure_download&website_url_honeypot=${encodeURIComponent(honeypot)}"

                                    // Default to the primary URL
                                    var finalPdfUrl = data.brochurePdfUrl

                                    data.formFields.forEach { field ->
                                        // Handle both standard inputs and select dropdowns
                                        val element = document.getElementById(field.id) as? org.w3c.dom.HTMLElement
                                        val valString = when (element) {
                                            is HTMLInputElement -> element.value
                                            is org.w3c.dom.HTMLSelectElement -> element.value
                                            else -> ""
                                        }

                                        // ROUTING LOGIC: If this is the audience dropdown, check the value
                                        if (field.id == "audienceType" && valString == "veterans" && data.secondaryBrochurePdfUrl != null) {
                                            finalPdfUrl = data.secondaryBrochurePdfUrl
                                        }

                                        formData += "&${encodeURIComponent(field.id)}=${encodeURIComponent(valString)}"
                                    }

                                    val requestInit = RequestInit(
                                        method = "POST",
                                        mode = org.w3c.fetch.RequestMode.NO_CORS,
                                        headers = json("Content-Type" to "application/x-www-form-urlencoded"),
                                        body = formData
                                    )
                                    window.fetch(data.formProcessingEndpoint, requestInit).then {
                                        isSubmitting = false
                                        formSubmitted = true
                                        val downloadLink = document.createElement("a") as HTMLAnchorElement
                                        downloadLink.href = finalPdfUrl // Use the routed URL!
                                        downloadLink.setAttribute("download", "")
                                        downloadLink.target = "_blank"
                                        document.body?.appendChild(downloadLink)
                                        downloadLink.click()
                                        document.body?.removeChild(downloadLink)

                                        // Reset fields
                                        data.formFields.forEach { field ->
                                            val element = document.getElementById(field.id) as? org.w3c.dom.HTMLElement
                                            when (element) {
                                                is HTMLInputElement -> element.value = ""
                                                is org.w3c.dom.HTMLSelectElement -> element.value = ""
                                            }
                                        }
                                        // Reset the honeypot just in case
                                        (document.getElementById("brochure_website_url_honeypot") as? HTMLInputElement)?.value = ""

                                    }.catch { isSubmitting = false; window.alert(data.alertErrorMessage) }
                                }
                            }) {
                                // 4. Inject the stealthy 1x1 Honeypot Input right at the start of the form
                                Input(
                                    type = InputType.Text,
                                    attrs = Modifier.opacity(0).position(Position.Absolute).top(0.px).left(0.px).width(1.px)
                                        .height(1.px).styleModifier {
                                            property("pointer-events", "none")
                                        }.toAttrs {
                                            attr("name", "website_url_honeypot")
                                            attr("id", "brochure_website_url_honeypot")
                                            attr("tabindex", "-1")
                                            attr("autocomplete", "off")
                                        }
                                )

                                Column(modifier = Modifier.fillMaxWidth().gap(16.px)) {
                                    data.formFields.forEach { fieldConfig ->
                                        key(fieldConfig.id) {
                                            DynamicFormField(
                                                fieldConfig = fieldConfig,
                                                palette = palette,
                                                data = data,
                                                parsedFonts = parsedFonts
                                            )
                                        }
                                    }
                                    Button(
                                        attrs = BrochureSubmitButtonStyle.toModifier().fillMaxWidth()
                                            .backgroundColor(palette.brand.primary).margin(top = 10.px, bottom = 10.px)
                                            .color(Colors.White).padding(topBottom = 14.px, leftRight = 24.px)
                                            .borderRadius(8.px).border(0.px).fontFamily(*parsedFonts).fontSize(16.px)
                                            .fontWeight(FontWeight.SemiBold)
                                            .cursor(if (isSubmitting) Cursor.NotAllowed else Cursor.Pointer)
                                            .opacity(if (isSubmitting) 0.6f else 1f).textAlign(TextAlign.Center)
                                            .toAttrs {
                                                attr("type", "submit"); if (isSubmitting) attr(
                                                "disabled", "true"
                                            )
                                            }) {
                                        Text(if (isSubmitting) data.buttonSubmittingText else data.buttonText)
                                    }
                                    if (formSubmitted && data.inlineSuccessMessage.isNotBlank()) SpanText(
                                        text = data.inlineSuccessMessage,
                                        modifier = Modifier.fontFamily(*parsedFonts).color(Color.rgb(0x2E8B57))
                                            .fontSize(14.px).fontWeight(FontWeight.SemiBold)
                                    )
                                    SpanText(
                                        text = data.privacyDisclaimer,
                                        modifier = Modifier.fontFamily(*parsedFonts).fontSize(14.px)
                                            .color(palette.custom.darkText.toRgb().copyf(alpha = 0.9f))
                                    )
                                }
                            }
                        }

                        if (data.imagePath.isNotBlank()) {
                            Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                                Image(
                                    src = data.imagePath,
                                    alt = data.imageAltText,
                                    modifier = Modifier.fillMaxWidth().maxWidth(350.px)
                                        .boxShadow(0.px, 20.px, 25.px, 0.px, Colors.Black.copyf(alpha = 0.15f))
                                )
                            }
                        }
                    }
                }

                if (formSubmitted) {
                    Box(
                        modifier = Modifier.position(Position.Fixed).top(0.px).left(0.px).fillMaxSize().zIndex(1000)
                            .backgroundColor(Color.argb(0.6f, 0f, 0f, 0f)).onClick { formSubmitted = false },
                        contentAlignment = Alignment.Center
                    ) {
                        Column(
                            modifier = Modifier.width(90.percent).maxWidth(500.px).backgroundColor(Colors.White)
                                .borderRadius(8.px).padding(32.px)
                                .boxShadow(0.px, 10.px, 30.px, color = Color.argb(0.3f, 0f, 0f, 0f))
                                .onClick { it.stopPropagation() }, horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            Box(
                                modifier = Modifier.size(64.px).borderRadius(50.percent)
                                    .backgroundColor(Color.rgb(0xE8F5E9)).margin(bottom = 16.px),
                                contentAlignment = Alignment.Center
                            ) {
                                SpanText(
                                    text = data.modalIconText,
                                    modifier = Modifier.color(Color.rgb(0x2E8B57)).fontSize(32.px)
                                        .fontWeight(FontWeight.Bold)
                                )
                            }
                            SpanText(
                                text = data.modalTitle,
                                modifier = Modifier.fontFamily(*parsedFonts).fontSize(24.px).fontWeight(FontWeight.Bold)
                                    .color(Color.rgb(0x333333)).margin(bottom = 8.px).textAlign(TextAlign.Center)
                            )
                            SpanText(
                                text = data.modalDescription,
                                modifier = Modifier.fontFamily(*parsedFonts).fontSize(16.px).lineHeight(1.5)
                                    .textAlign(TextAlign.Center).color(Color.rgb(0x666666)).margin(bottom = 24.px)
                            )
                            Button(
                                attrs = BrochureSubmitButtonStyle.toModifier().backgroundColor(palette.brand.primary)
                                    .color(Colors.White).border(0.px).borderRadius(6.px).fontSize(18.px)
                                    .fontFamily(*parsedFonts).fontWeight(FontWeight.SemiBold)
                                    .padding(topBottom = 10.px, leftRight = 24.px).cursor(Cursor.Pointer)
                                    .toAttrs { onClick { formSubmitted = false } }) { Text(data.modalCloseButtonText) }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun DynamicFormField(
    fieldConfig: FormFieldConfig, palette: SitePalette, data: ResourcesBrochureDownloadData, parsedFonts: Array<String>
) {
    var text by remember { mutableStateOf("") }
    var isTouched by remember { mutableStateOf(false) }
    var isFocused by remember { mutableStateOf(false) }

    val isEmptyError = fieldConfig.isRequired && isTouched && text.isBlank()
    val isEmailError = fieldConfig.inputType == InputType.Email && isTouched && text.isNotBlank() && !text.contains("@")
    val hasError = isEmptyError || isEmailError
    val borderColor = if (hasError) Colors.Red else if (isFocused) palette.brand.primary else Colors.DarkGray

    var inputModifier =
        Modifier.id(fieldConfig.id).fillMaxWidth().padding(12.px).backgroundColor(Colors.White).borderRadius(6.px)
            .border(1.px, LineStyle.Solid, borderColor).outline(style = LineStyle.None).fontFamily(*parsedFonts)
            .fontSize(16.px).color(palette.custom.darkText)
            .transition(Transition.of("border-color", 200.ms), Transition.of("box-shadow", 200.ms))
    if (hasError && isFocused) inputModifier =
        inputModifier.boxShadow(0.px, 0.px, 0.px, 3.px, Colors.Red.toRgb().copyf(alpha = 0.15f))
    else if (isFocused) inputModifier =
        inputModifier.boxShadow(0.px, 0.px, 0.px, 3.px, palette.brand.primary.toRgb().copyf(alpha = 0.2f))

    Column(modifier = Modifier.fillMaxWidth()) {
        if (fieldConfig.label.isNotBlank()) {
            Label(
                forId = fieldConfig.id,
                attrs = Modifier.fontFamily(*parsedFonts).color(palette.custom.darkText).fontSize(14.px)
                    .fontWeight(FontWeight.SemiBold).margin(bottom = 6.px).toAttrs()
            ) {
                Text(fieldConfig.label)
                if (fieldConfig.isRequired) SpanText(data.requiredFieldIndicator, modifier = Modifier.color(Colors.Red))
                else SpanText(
                    data.optionalFieldIndicator,
                    modifier = Modifier.color(palette.custom.darkText.toRgb().copyf(alpha = 0.6f))
                        .fontWeight(FontWeight.Normal)
                )
            }

            if (fieldConfig.isDropdown) {
                Select(
                    attrs = inputModifier.toAttrs {
                        if (fieldConfig.isRequired) required()
                        attr("name", fieldConfig.id)
                        onFocusIn { isFocused = true }
                        onFocusOut { isFocused = false; isTouched = true }
                        onChange { text = it.value ?: ""; isTouched = true }
                    }) {
                    Option(
                        value = "", attrs = Modifier.toAttrs {
                            attr("disabled", "true"); attr("selected", "true"); attr(
                            "hidden", "true"
                        )
                        }) {
                        Text(fieldConfig.placeholder)
                    }
                    fieldConfig.dropdownOptions.forEach { option ->
                        key(option.value) {
                            Option(value = option.value) {
                                Text(option.label)
                            }
                        }
                    }
                }
            } else if (fieldConfig.inputType != null) {
                Input(
                    type = fieldConfig.inputType, attrs = inputModifier.toAttrs {
                        placeholder(fieldConfig.placeholder); if (fieldConfig.isRequired) required(); attr(
                        "name", fieldConfig.id
                    ); onFocusIn { isFocused = true }; onFocusOut { isFocused = false; isTouched = true }; onInput {
                        text = it.value; isTouched = true
                    }
                    })
            }

            if (isEmptyError) SpanText(
                fieldConfig.errorMessageEmpty,
                modifier = Modifier.fontFamily(*parsedFonts).color(Colors.Red).fontSize(14.px).margin(top = 4.px)
            )
            else if (isEmailError) SpanText(
                fieldConfig.errorMessageInvalid,
                modifier = Modifier.fontFamily(*parsedFonts).color(Colors.Red).fontSize(14.px).margin(top = 4.px)
            )
        }
    }
}

// ==========================================
// CMS DATA MODEL & CONSTRAINTS
// ==========================================

/**
 * Data class representing all CMS-configurable content for the Brochure Download Section.
 * * @property imagePath Path to the brochure mockup image.
 * CMS CONSTRAINTS FOR IMAGE:
 * - Format: MUST be a transparent PNG or WebP. Do not use JPEG, as the image will be placed on a white card with its own drop shadow. A white/solid background on the image will break the floating 3D effect.
 * - Aspect Ratio: 8.5:11 (Standard Letter/Brochure Portrait) or 3:4.
 * - Resolution: Minimum width of 600px, maximum width 1200px (to remain crisp on Retina displays without bloating load times).
 * - Cropping: Ensure the image is tightly cropped to the edges of the brochure/book with no empty padding inside the file.
 */
/**
 * Defines a single dynamic input field configuration to be injected into the form.
 */
data class FormFieldConfig(
    val id: String,
    val label: String,
    val placeholder: String,
    val inputType: InputType<String>? = null, // Nullable now, since Select isn't a standard text InputType
    val isRequired: Boolean,
    val isDropdown: Boolean = false,
    val dropdownOptions: List<FormFieldOption> = emptyList(),
    val errorMessageEmpty: String = "This field is required.",
    val errorMessageInvalid: String = "Please enter a valid value."
)

data class FormFieldOption(val label: String, val value: String)

data class ResourcesBrochureDownloadData(
    val sectionId: String? = "resources-brochure-download",
    val mainHeading: String = "DOWNLOAD OUR BROCHURE",
    val subHeading: String = "Get a comprehensive guide to our innovative MyHand<sup>®</sup> System.",
    val fontFamily: String = "Inter, sans-serif",
    val cardTitle: String = "Access the Brochure",
    val cardSubtitle: String = "Complete these quick fields to download your copy instantly.",
    val formFields: List<FormFieldConfig> = listOf(
        FormFieldConfig(
            id = "fullName",
            label = "Full Name",
            placeholder = "Enter your full name",
            inputType = InputType.Text,
            isRequired = true
        ), FormFieldConfig(
            id = "workEmail",
            label = "Work Email",
            placeholder = "Enter your professional email address",
            inputType = InputType.Email,
            isRequired = true,
            errorMessageInvalid = "Please enter a valid email address."
        ), FormFieldConfig(
            id = "companyName",
            label = "Company Name",
            placeholder = "Enter your company name",
            inputType = InputType.Text,
            isRequired = false
        )
    ),
    val requiredFieldIndicator: String = " *",
    val optionalFieldIndicator: String = " (Optional)",
    val buttonText: String = "DOWNLOAD BROCHURE NOW (PDF)",
    val buttonSubmittingText: String = "DOWNLOADING BROCHURE...",
    val inlineSuccessMessage: String = "",
    val alertErrorMessage: String = "Unable to process request. Please check your connection and try again.",
    val modalIconText: String = "✓",
    val modalTitle: String = "Download Initiated",
    val modalDescription: String = "Thank you for your interest in the MyHand® System. Your information has been securely recorded, and your brochure download has started automatically. If prompted, please allow downloads in your browser.",
    val modalCloseButtonText: String = "Close",
    val privacyDisclaimer: String = "* Indicates a required field. We respect your privacy.",
    val formProcessingEndpoint: String = "https://script.google.com/a/macros/iregained.rehab/s/AKfycbxGjRZicaotnihoNv7zquaM-Pt2mzYPURWqIT-eeCsqwUow8Hvlw-rq1ih7FNswyrx3/exec",

    // Primary URL (Default)
    val brochurePdfUrl: String = "/documents/IRegained_MyHand_System_Brochure.pdf",
    // Secondary URL (For routing)
    val secondaryBrochurePdfUrl: String? = null,

    val imagePath: String = Res.Images.Resources.Brochure.BROCHURE_COVER,
    val imageAltText: String = "Product Brochure Preview"
)

// REGION SWITCHBOARD CONFIGURATIONS
private val usBrochureData = ResourcesBrochureDownloadData(
    brochurePdfUrl = "/documents/IRegained_MyHand_Brochure-2026.pdf",
    secondaryBrochurePdfUrl = "/documents/IRegained_MyHand_VA_Brochure-2026.pdf",
    formFields = listOf(
        FormFieldConfig(
            id = "audienceType",
            label = "I am a...",
            placeholder = "Select an option",
            isDropdown = true,
            isRequired = true,
            dropdownOptions = listOf(
                FormFieldOption("Clinician / Healthcare Provider", "clinical"),
                FormFieldOption("Veterans Affairs", "veterans")
            )
        ), FormFieldConfig(
            id = "fullName",
            label = "Full Name",
            placeholder = "Enter your full name",
            inputType = InputType.Text,
            isRequired = true
        ), FormFieldConfig(
            id = "workEmail",
            label = "Email Address",
            placeholder = "Enter your email address",
            inputType = InputType.Email,
            isRequired = true,
            errorMessageInvalid = "Please enter a valid email address."
        )
    )
)

private val canadaBrochureData = ResourcesBrochureDownloadData(
    brochurePdfUrl = "/documents/IRegained_MyHand_Brochure-2026.pdf",
)

private val indiaBrochureData = ResourcesBrochureDownloadData(
    brochurePdfUrl = "/documents/IRegained_IN_Brochure-2026.pdf",
)

// DYNAMIC GETTER
val defaultResourcesBrochureDownloadData: ResourcesBrochureDownloadData
    get() = when (RegionConfig.currentRegion) {
        Region.US -> usBrochureData
        Region.CANADA -> canadaBrochureData
        Region.INDIA -> indiaBrochureData
    }